import { useMemo, useState } from 'react';

import { IncludedEventDates } from '#components/molecules/IncludedEventDates/IncludedEventDates.js';
import { useShowEventDate } from '@givebacks/api/store';
import { DateTime } from 'luxon';
import { Modal, ModalProps, Stack, Text, TicketCard, TicketCardProps } from 'ui';

import {
  CheckoutButtons,
  CheckoutButtonsProps,
} from '../../molecules/Cart/buttons/CheckoutButtons/CheckoutButtons';
import { CheckoutCartOnOptions } from '../../molecules/Cart/buttons/CheckoutButtons/CheckoutButtons.types';
import { StoreItemModals, StoreItemModalsProps } from './StoreItemModals/StoreItemModals';

interface TicketedEventsModalContentProps
  extends Omit<TicketedEventsModalProps, 'onClick' | 'opened'> {
  checkoutButtonsProps?: Partial<CheckoutButtonsProps>;
}

const TicketedEventsModalContent = ({
  storeId,
  storeItemId,
  eventId,
  eventDateId,
  title,
  price,
  description,
  inventoryAvailable,
  eventDate,
  sellDate,
  openAdditionalFields,
  onClose,
  eventDateTimeCard,
  includedEventDates,
  checkoutButtonsProps,
}: TicketedEventsModalContentProps) => {
  // Don't set the default quantity to 1 is there is no inventory available
  const [quantity, setQuantity] = useState(inventoryAvailable === 0 ? 0 : 1);

  const { data: eventDateData } = useShowEventDate({
    causeId: storeId,
    eventId: eventId,
    dateId: eventDateId,
  });

  const cartItems = new Array(quantity).fill({
    price,
    quantity: 1,
    store_item_id: storeItemId,
    event_date_id: eventDateId,
  });

  const showTicketDetails = useMemo(() => {
    return !!description || !!eventDateTimeCard;
  }, [description, eventDateTimeCard]);

  const showIncludedEventDates = useMemo(() => {
    if (!includedEventDates) return false;

    return includedEventDates.length > 0;
  }, [includedEventDates]);

  return (
    <>
      <Stack gap="lg">
        <TicketCard
          opened
          quantity={quantity}
          onQuantityChange={setQuantity}
          title={title}
          price={price}
          inventoryAvailable={inventoryAvailable}
          eventDate={eventDate}
          sellDate={sellDate}
        />
        {showTicketDetails && (
          <Stack gap="md">
            <Text fz="lg" fw="600">
              Ticket Details
            </Text>

            {eventDateTimeCard?.({
              startingAt: eventDateData?.event_date?.starting_at ?? '',
              endingAt: eventDateData?.event_date?.ending_at ?? '',
            })}

            {!!description && (
              <Text fz="sm" px="md">
                {description}
              </Text>
            )}
          </Stack>
        )}
        {showIncludedEventDates && (
          <Stack gap="md">
            <Text fz="lg" fw="600">
              Included Event Dates
            </Text>
            <IncludedEventDates
              eventDates={includedEventDates}
              causeId={storeId}
              eventId={eventId}
            />
          </Stack>
        )}
      </Stack>

      <CheckoutButtons
        storeId={storeId}
        storeItemId={storeItemId}
        quantity={quantity}
        onOptions={openAdditionalFields}
        onCartCreate={onClose}
        cartItems={cartItems}
        {...checkoutButtonsProps}
      />
    </>
  );
};

interface TicketedEventsModalProps
  extends Omit<TicketedEventsModalCardProps, 'eventTitle'>,
    Pick<ModalProps, 'opened' | 'onClose'> {
  itemName?: string;
  openAdditionalFields?: CheckoutCartOnOptions;
}

const TicketedEventsModal = ({
  opened,
  onClose,
  title,
  storeId,
  storeItemId,
  eventId,
  eventDate,
  eventDateId,
  price,
  description,
  inventoryAvailable,
  sellDate,
  openAdditionalFields,
  eventDateTimeCard,
  includedEventDates,
  checkoutButtonsProps,
  ...props
}: TicketedEventsModalProps) => {
  if (!storeId || !storeItemId) return null;

  return (
    <Modal title={`${title} Ticket Details`} opened={opened} onClose={onClose} {...props}>
      <TicketedEventsModalContent
        storeId={storeId}
        storeItemId={storeItemId}
        eventId={eventId}
        eventDateId={eventDateId}
        title={title}
        price={price}
        description={description}
        inventoryAvailable={inventoryAvailable}
        eventDate={eventDate}
        sellDate={sellDate}
        openAdditionalFields={openAdditionalFields}
        onClose={onClose}
        eventDateTimeCard={eventDateTimeCard}
        includedEventDates={includedEventDates}
        checkoutButtonsProps={checkoutButtonsProps}
      />
    </Modal>
  );
};

interface TicketedEventsModalCardProps
  extends Omit<TicketCardProps, 'opened' | 'onClick'>,
    Omit<ModalProps, 'title' | 'opened' | 'onClose'> {
  /**
   * Event Id
   */
  storeId: string;

  /**
   * Ticket Id
   */
  storeItemId: string;

  /**
   * Event Id
   */
  eventId: string;

  /**
   * Event Date Id
   */
  eventDateId: string;

  /**
   * A render prop for the event date time card component to be passed through
   */
  eventDateTimeCard?: ({
    startingAt,
    endingAt,
  }: {
    startingAt: string;
    endingAt: string;
  }) => JSX.Element;

  eventTitle: string;

  /**
   * Included event dates for season tickets
   */
  includedEventDates?: string[];

  /**
   * Props to pass to the checkout buttons
   */
  checkoutButtonsProps?: Partial<CheckoutButtonsProps>;

  /**
   * Props to pass to the additional fields footer
   */
  additionalFieldsProps?: StoreItemModalsProps['additionalFieldsProps'];
}

const TicketedEventsModalsCard = ({
  storeId,
  storeItemId,
  eventId,
  eventDateId,
  eventTitle,
  title,
  description,
  price,
  inventoryAvailable,
  eventDate,
  sellDate,
  eventDateTimeCard,
  includedEventDates,
  checkoutButtonsProps,
  additionalFieldsProps,
  ...modalProps
}: TicketedEventsModalCardProps) => {
  const additionalFieldTicketTitle = useMemo(() => {
    const startDate = eventDate ? DateTime.fromISO(eventDate).toFormat('ccc, LLL d') : undefined;
    const startTime = eventDate
      ? DateTime.fromISO(eventDate)
          .toLocaleString(DateTime.TIME_SIMPLE)
          .replace(' AM', 'am')
          .replace(' PM', 'pm')
      : undefined;
    const timezone = eventDate ? DateTime.fromISO(eventDate).toFormat('ZZZZ') : undefined;

    if (eventDate === 'all') {
      return [eventTitle, title];
    }

    return [eventTitle, title, startDate, `${startTime} ${timezone}`];
  }, [eventDate, eventTitle, title]);

  return (
    <StoreItemModals
      storeId={storeId}
      storeItemId={storeItemId}
      additionalFieldsProps={{
        fieldLabel: 'Ticket',
        fieldDescription: additionalFieldTicketTitle.join('   ·   '),
        ...additionalFieldsProps,
      }}
      modalProps={modalProps}
    >
      {({ openModalById, openAdditionalFields, ...restStoreItemProps }) => (
        <>
          <TicketCard
            onClick={() => openModalById('StoreItem')}
            title={title}
            description={description}
            price={price}
            inventoryAvailable={inventoryAvailable}
            eventDate={eventDate}
            sellDate={sellDate}
          />
          <TicketedEventsModal
            title={title}
            description={description}
            price={price}
            inventoryAvailable={inventoryAvailable}
            eventDate={eventDate}
            sellDate={sellDate}
            storeId={storeId}
            storeItemId={storeItemId}
            eventId={eventId}
            eventDateId={eventDateId}
            itemName={title}
            // This gets passed down to checkout out buttons as an interceptor
            openAdditionalFields={openAdditionalFields}
            eventDateTimeCard={eventDateTimeCard}
            includedEventDates={includedEventDates}
            checkoutButtonsProps={checkoutButtonsProps}
            {...restStoreItemProps}
            {...modalProps}
          />
        </>
      )}
    </StoreItemModals>
  );
};

export { TicketedEventsModalsCard };
